import { Button, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { X, Check } from "react-feather";
import styles from "./Popup.module.css";
import { authData } from "../GetAuth";
import { Close } from "@mui/icons-material";
import SharedPasswordFields, { adminAccountsLookup } from "../SharedPasswordFields";

const AddCustomerForm = ({ onSubmitAddForm, onSubmitEditForm, setFormOpen, setSnackbarOpen, setSnackbarMessage, setSnackbarType, setLoading, editData }) => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const [isRegCompany, setIsRegCompany] = useState("");
  const [selectPCountry, setSelectPCountry] = useState('GB');

  useEffect(() => {
    if (auth?.company && auth?.company?.pickup_country) {
      setSelectPCountry(auth?.company?.pickup_country);
    }
  }, [auth?.company]);

  const { handleSubmit, control, setValue, setError, watch, formState: { errors } } = useForm({
    mode: "onChange",
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      address: "",
      post_code: "",
      registered_company: "",
      company_shipping: "",
      company_name: "",
    },
  });

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("address", place.formatted_address);

      setValue("latitude", place.geometry.location.lat());
      setValue("longitude", place.geometry.location.lng());

      place.address_components.forEach((item) => {
        if (item.types.indexOf("postal_code") > -1) {
          setValue("post_code", item.long_name);
        }
      });
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: selectPCountry },
    },
  });

  useEffect(() => {
    if (editData) {
      setValue("name", editData?.name);
      setValue("email", editData?.email);
      setValue("phone", editData?.phone);
      setValue("post_code", editData?.post_code);
      setValue("address", editData?.address);
      setValue("registered_company", editData?.registered_company);
      if (editData.registered_company === "Yes") {
        setValue("company_shipping", editData?.company_shipping);
        setValue("company_name", editData?.company_name);
        setIsRegCompany("Yes");
      }
    }
  }, [editData, setValue]);

  const onSubmitLocal = (data) => {
    setLoading(true);

    if (editData) {
      axios.post(process.env.REACT_APP_API_URL + "v1/customers/" + editData?.id, data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setFormOpen(false);
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");

          onSubmitEditForm(response.data.data);
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
        if (response.data.status === "VALIDATIONERROR") {
          const serverErrors = response.data.errors;

          // Loop backend validation errors
          Object.keys(serverErrors).forEach((field) => {
            setError(field, {
              type: "server",
              message: serverErrors[field][0], // Laravel style
            });
          });
        }
      }).catch((error) => {
        setLoading(false);
        // Show every failure (expired login, server error, network), not only ones with `messages`.
        setSnackbarOpen(true);
        setSnackbarMessage(error?.response?.data?.messages || error?.response?.data?.message || error?.message || "Something went wrong. Please try again.");
        setSnackbarType("error");
      });
    } else {
      axios.post(process.env.REACT_APP_API_URL + "v1/customers", data, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      }).then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setFormOpen(false);
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");

          onSubmitAddForm(response.data.data);
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
        if (response.data.status === "VALIDATIONERROR") {
          const serverErrors = response.data.errors;

          // Loop backend validation errors
          Object.keys(serverErrors).forEach((field) => {
            setError(field, {
              type: "server",
              message: serverErrors[field][0], // Laravel style
            });
          });
        }
      }).catch((error) => {
        setLoading(false);
        // Show every failure (expired login, server error, network), not only ones with `messages`.
        setSnackbarOpen(true);
        setSnackbarMessage(error?.response?.data?.messages || error?.response?.data?.message || error?.message || "Something went wrong. Please try again.");
        setSnackbarType("error");
      });
    }
  };

  return (<>
    <DialogTitle id="alert-dialog-title" className={`${styles.PopupTitleBar}`}>
      {editData ? "Update Customer" : "Add Customer"}
      <IconButton onClick={(e) => setFormOpen(false)} className={`${styles.PopupCloseBU}`} aria-label="Close"><Close /></IconButton>
    </DialogTitle>
    <DialogContent className={`${styles.PopupContent}`}>
      <form onSubmit={handleSubmit(onSubmitLocal)}>
        <div className={`${styles.FormMainRow}`}>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              name="name"
              control={control}
              rules={{ required: "This field is Required" }}
              render={({ field }) => (<TextField {...field} label="Full Name*" variant="outlined" fullWidth />)}
            />
            {errors.name && (<span className={`${styles.ErrorM}`}>{errors?.name?.message}</span>)}
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              name="email"
              control={control}
              rules={{
                required: "This field is Required",
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: "Invalid email address",
                },
              }}
              render={({ field }) => (<TextField {...field} label="Email*" variant="outlined" disabled={!!editData} fullWidth />)}
            />
            {errors.email && (<span className={`${styles.ErrorM}`}>{errors?.email?.message}</span>)}
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              name="phone"
              control={control}
              rules={{ required: "This field is Required" }}
              render={({ field }) => (<TextField {...field} label="Phone*" variant="outlined" fullWidth />)}
            />
            {errors.phone && (<span className={`${styles.ErrorM}`}>{errors.phone.message}</span>)}
          </div>
          {!editData && (
            <SharedPasswordFields
              control={control}
              watch={watch}
              errors={errors}
              lookupAccounts={adminAccountsLookup(API_TOKEN)}
              groupClassName={styles.FormGroupHalf}
              fullGroupClassName={styles.FormGroupFull}
              errorClassName={styles.ErrorM}
            />
          )}
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              name="address"
              control={control}
              rules={{ required: "This field is Required" }}
              render={({ field }) => (<TextField {...field} label="Address*" variant="outlined" inputRef={ref} placeholder="" fullWidth />)}
            />
            {errors.address && (<span className={`${styles.ErrorM}`}>{errors?.address?.message}</span>)}
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <Controller
              name="post_code"
              control={control}
              rules={{ required: "This field is Required" }}
              render={({ field }) => (<TextField {...field} label="Post Code*" variant="outlined" fullWidth />)}
            />
            {errors.post_code && (<span className={`${styles.ErrorM}`}>{errors?.post_code?.message}</span>)}
          </div>
          <div className={`${styles.FormGroupHalf}`}>
            <FormControl fullWidth>
              <InputLabel id="registered-company-label">Do you have a Registered Company?*</InputLabel>
              <Controller
                name="registered_company"
                control={control}
                rules={{ required: "This field is Required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="registered-company-label"
                    label="Do you have a Registered Company?*"
                    onChange={(e) => {
                      field.onChange(e);
                      setIsRegCompany(e.target.value);
                    }}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            {errors.registered_company && (<span className={`${styles.ErrorM}`}>{errors?.registered_company?.message}</span>)}
          </div>
          {isRegCompany === "Yes" && (<div className={`${styles.FormGroupHalf}`}>
            <FormControl fullWidth>
              <InputLabel id="company-shipping-label">If Yes, are you shipping the goods in the name of the company?*</InputLabel>
              <Controller
                name="company_shipping"
                control={control}
                rules={{ required: "This field is Required" }}
                render={({ field }) => (
                  <Select {...field} labelId="company-shipping-label" label="If Yes, are you shipping the goods in the name of the company?*">
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </Select>
                )}
              />
            </FormControl>
            {errors.company_shipping && (<span className={`${styles.ErrorM}`}>{errors?.company_shipping?.message}</span>)}
          </div>)}
          {isRegCompany === "Yes" && (<div className={`${styles.FormGroupHalf}`}>
            <Controller
              name="company_name"
              control={control}
              rules={{ required: "This field is Required" }}
              render={({ field }) => (<TextField {...field} label="Registered Company Name*" variant="outlined" fullWidth />)}
            />
            {errors.company_name && (<span className={`${styles.ErrorM}`}>{errors?.company_name?.message}</span>)}
          </div>
          )}
        </div>

        <div className={`${styles.ButtonAction}`}>
          <Button className={`${styles.CancelPopupBU}`} startIcon={<X size={16} />} onClick={(e) => setFormOpen(false)}>Cancel</Button>
          <Button className={`${styles.SubmitPopupBU}`} startIcon={<Check size={16} />} type="submit">{editData ? "Update" : "Add"}</Button>
        </div>
      </form>
    </DialogContent>
  </>);
};

export default AddCustomerForm;